<template>
    <div v-if="needsPortalUrl" class="float-end d-flex align-items-end gap-2">
        <div class="thetalake-portal text-start">
            <label class="form-label small mb-1" :for="portalInputId">Theta Lake Portal URL</label>
            <div class="input-group input-group-sm">
                <input :id="portalInputId"
                       v-model="portalInput"
                       type="text"
                       class="form-control"
                       placeholder="your-instance"
                       autocomplete="off"
                       spellcheck="false"
                       :aria-describedby="portalSuffixId"
                       @keyup.enter="connectDisconnect">
                <span class="input-group-text" :id="portalSuffixId">.{{ thetalakeDomain }}</span>
            </div>
        </div>
        <button type="button" @click.stop.prevent="connectDisconnect"
                class="btn btn-sm btn-success" :disabled="!portalUrl" :title="buttonTitle">
            {{ label }}
        </button>
    </div>
    <button v-else type="button" @click.stop.prevent="connectDisconnect" :class="{
                'float-end': true, btn:true, 'btn-sm': true,
                'btn-success': connected === false,
                'btn-danger': connected === true
            }">
        {{ label }}
    </button>
</template>

<style scoped>
    /* Wide enough that a realistic tenant name is readable next to the suffix; the
       float-end wrapper would otherwise shrink the input to its placeholder. */
    .thetalake-portal {
        min-width: 14rem;
    }
</style>

<script>
    import {mapGetters} from 'vuex'
    import {setPortalUrl} from '@/services/ThetalakePortalUrl'

    // DEV-2908: Theta Lake's authorize endpoint needs `theta_lake_portal_url`, the customer's own
    // instance. We ask for the subdomain label and hold the domain constant, so a free-text field
    // cannot point at developer.thetalake.ai, our developer site, where a customer has no account.
    //
    // ⚠ Cannot express a host that is not `<label>.thetalake.ai`; a pasted one is silently
    // mangled. Fix is to accept a bare label *or* a full hostname — see DEV-2908 Notes →
    // *The portal field assumes a domain*.
    const THETALAKE_DOMAIN = 'thetalake.ai';

    /**
     * Reduce whatever the user typed to a bare instance label, accepting `useast`,
     * `useast.thetalake.ai` or a full pasted URL. Invalid characters are dropped rather than
     * escaped, so a malformed entry yields an empty value and Connect stays disabled instead of
     * sending Theta Lake a bad portal URL.
     */
    function instanceFromInput(raw) {
        return String(raw || '')
            .trim()
            .toLowerCase()
            .replace(/^[a-z]+:\/\//, '')          // scheme
            .split('/')[0]                         // path / fragment
            .split(':')[0]                         // port
            .replace(new RegExp(`\\.?${THETALAKE_DOMAIN.replace('.', '\\.')}$`), '')
            .replace(/[^a-z0-9-]/g, '')            // anything not a DNS label char
            .replace(/^-+|-+$/g, '');              // leading/trailing hyphens
    }

    // The template has two mutually exclusive roots on purpose. Every other integration keeps
    // the bare <button> as this component's root element, so class passthrough (PowerupCard
    // passes `full-width`), IntegrationItem's `$el.classList.add('float-end')`, and the
    // `.integration-list>li>.float-end` mobile rule all behave exactly as before. Only Theta
    // Lake's pre-connect state introduces a wrapper.
    //
    // Keep the template free of root-level comments: a comment node counts toward the root
    // count, which turns the v-if/v-else chain into a fragment and silently disables Vue 3's
    // automatic attribute inheritance.

    // Distinguishes instances for label/input `for` association. A module-level counter rather
    // than Vue's internal `_uid`, which is a Vue 2 field this app (Vue 3 via @vue/compat) does
    // not expose.
    let instanceSeq = 0;

    export default {
        props: ['service', 'team', 'beforeAuth'],
        data() {
            return {
                // No default: the portal URL is per-customer, so the user always types it.
                portalInput: '',
                uid: ++instanceSeq
            }
        },
        computed: {
            connected() { return !!this.service && !!this.service.isConnected(this.team) },
            ...mapGetters({
                vendor: 'vendor',
                afterOauthData: 'afterOauthData'
            }),
            /**
             * DEV-2908: Theta Lake needs the customer's portal URL before authorization can
             * start, so a disconnected Theta Lake account gets an input alongside Connect.
             *
             * Keyed off the service's apiName rather than a new prop, so every existing caller
             * (Mailchimp, HubSpot, Sniply…) keeps its plain two-state button with no change at
             * the call site.
             */
            needsPortalUrl() {
                return !this.connected && !!this.service && this.service.apiName === 'thetalake';
            },
            portalUrl() {
                const instance = instanceFromInput(this.portalInput);
                return instance ? `https://${instance}.${THETALAKE_DOMAIN}` : '';
            },
            thetalakeDomain() { return THETALAKE_DOMAIN },
            // Unique per instance: the integrations list renders one of these per account, so a
            // static id would break label/input association for every row after the first.
            portalInputId() { return `thetalake-portal-${this.uid}` },
            portalSuffixId() { return `thetalake-portal-suffix-${this.uid}` },
            buttonTitle() {
                if (!this.needsPortalUrl) return null;
                return this.portalUrl
                    ? `Connect to ${this.portalUrl}`
                    : 'Enter your Theta Lake portal URL first.';
            },
            label() {
                return this.connected ? 'Disconnect' : 'Connect';
            }
        },
        methods: {
            async connectDisconnect() {
                if (this.connected) {
                    this.service.disconnect(this.team);
                    return;
                }

                if (this.needsPortalUrl) {
                    // Guard rather than rely on :disabled alone — the input's Enter handler
                    // reaches this method directly.
                    if (!this.portalUrl) return;
                    // Stored, not passed: the authorize round trip to Theta Lake sits between
                    // here and the callback, and their redirect returns `code`, `api_url` and
                    // `state` — never the portal URL. See ThetalakePortalUrl.js.
                    setPortalUrl(this.portalUrl);
                }

                if (this.beforeAuth) await this.beforeAuth();
                let iframed = this.vendor === 'hootsuite'
                setTimeout(() => this.service.connect(this.team, { iframed, afterOauthData: this.afterOauthData }), 5);
            }
        }
    }
</script>
